// src/pages/LavorazioniInCorso.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { caricaReportCompleto } from '../utils/reportCompleto';
import { Card, TableCell, TableHeadCell } from '../components/ui';

const API_BASE = '/api';

const LavorazioniInCorso = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [tableSource, setTableSource] = useState([]); // per la tabella (tutto)

  // Modale riassegnazione
  const [reassignModal, setReassignModal] = useState({
    open: false,
    item: null,
  });

  const [users, setUsers] = useState([]);
  const [reassignForm, setReassignForm] = useState({
    dateReceived: '',
    assignedTo: '',
    preventivoStatus: '',
    consuntivoStatus: '',
    orderStatus: '',
    materialeArrivato: '',
  });

  // FILTRI tabella
  const [filterText, setFilterText] = useState('');
  const [filterPreventivo, setFilterPreventivo] = useState('tutti');
  const [filterOrdine, setFilterOrdine] = useState('tutti');

  const navigate = useNavigate();

  // Deduplica per codice interno
  const dedupeByInternalCode = (data) => {
    const map = new Map();

    (data || []).forEach((item) => {
      const order = item.workOrder || {};
      const summary = item.summary || {};

      const code =
        order.internalCode ||
        summary.codiceInterno ||
        order.codiceInterno ||
        item._id;

      const key = code || item._id;
      const existing = map.get(key);

      if (!existing) {
        map.set(key, item);
      } else {
        const a = new Date(existing.generatedAt || 0).getTime();
        const b = new Date(item.generatedAt || 0).getTime();
        if (b > a) {
          map.set(key, item);
        }
      }
    });

    return Array.from(map.values());
  };

  // Carica lavorazioni in corso
  const loadInProgress = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/in-progress`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero delle lavorazioni in corso.'
        );
      }

      const data = await res.json();

      const openOrSuspended = (data || []).filter(
        (item) =>
          item.workOrder &&
          ['in_corso', 'sospesa'].includes(item.workOrder.status)
      );

      setTableSource(openOrSuspended);

      const unique = dedupeByInternalCode(openOrSuspended);
      setItems(unique);
    } catch (err) {
      console.error('Errore loadInProgress:', err);
      setError(err.message || 'Errore nella richiesta al server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInProgress();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatWorkStatus = (status) => {
    switch (status) {
      case 'aperta':
        return 'Aperta';
      case 'in_corso':
        return 'In corso';
      case 'sospesa':
        return 'Sospesa';
      case 'completata':
        return 'Completata';
      case 'annullata':
        return 'Annullata';
      default:
        return status || '-';
    }
  };

  const openReport = async (item) => {
    const order = item.workOrder || {};
    const isManutenzione = order.type === 'manutenzione';

    const targetRoute = isManutenzione
      ? '/lavorazioni-in-corso/manutenzioni'
      : '/lavorazioni-in-corso/vit';

    // l'elenco e' leggero: firme, formData e checks si chiedono adesso,
    // solo per la lavorazione che si sta aprendo
    const completo = await caricaReportCompleto(item);

    navigate(targetRoute, {
      state: {
        fromWorkInProgress: completo,
      },
    });
  };

  // ============================
  // RIASSEGNA VIT
  // ============================
  const openReassignModal = async (item) => {
    setError('');
    setReassignModal({ open: true, item });

    try {
      const res = await authFetch(`${API_BASE}/users`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nel caricamento utenti.');
      }
      const data = await res.json();

      const assegnabili = (data || []).filter((u) =>
        ['admin', 'gestione', 'tecnico', 'Tecnico'].includes(u.role)
      );

      setUsers(assegnabili);

      const dummy = assegnabili.find((u) => {
        const nome = (u.nome || '').toLowerCase();
        const cognome = (u.cognome || '').toLowerCase();
        const username = (u.username || '').toLowerCase();
        return (
          (nome === 'da' && cognome === 'assegnare') ||
          username.includes('daassegnare') ||
          username.includes('da.assegnare') ||
          username.includes('da_assegnare')
        );
      });

      setReassignForm({
        dateReceived: '',
        assignedTo: dummy ? dummy._id : '',
        preventivoStatus: '',
        consuntivoStatus: '',
        orderStatus: '',
        materialeArrivato: '',

      });
    } catch (err) {
      console.error('Errore caricamento utenti per riassegnazione:', err);
      setError(err.message || 'Errore nel caricamento della lista utenti.');
    }
  };

  const handleReassign = async () => {
    setError('');

    const item = reassignModal.item;
    if (!item || !item.workOrder) {
      setError('Lavorazione non valida per la riassegnazione.');
      return;
    }

    if (
      !reassignForm.dateReceived ||
      !reassignForm.assignedTo ||
      !reassignForm.preventivoStatus ||
      !reassignForm.orderStatus ||
      !reassignForm.materialeArrivato
    ) {
      setError(
        "Compila la data di ricezione, seleziona l'utente, lo stato del preventivo e lo stato dell'ordine."
      );
      return;
    }

    if (
      reassignForm.preventivoStatus === 'A consuntivo' &&
      !reassignForm.consuntivoStatus
    ) {
      setError('Se selezioni "A consuntivo" devi indicare "Si" o "No".');
      return;
    }

    const dateInterventoToSend = reassignForm.dateReceived;

    try {
      const res = await authFetch(`${API_BASE}/work/reassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workOrderId: item.workOrder._id,
          workInProgressId: item._id,
          dateReceived: reassignForm.dateReceived,
          dateIntervento: dateInterventoToSend,
          assignedTo: reassignForm.assignedTo,
          preventivoStatus: reassignForm.preventivoStatus,
          consuntivoStatus:
            reassignForm.preventivoStatus === 'A consuntivo'
              ? reassignForm.consuntivoStatus
              : undefined,
          orderStatus: reassignForm.orderStatus,
          materialeArrivato: reassignForm.materialeArrivato,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nella riassegnazione.');
      }

      setReassignModal({ open: false, item: null });
      await loadInProgress();
    } catch (err) {
      console.error('Errore handleReassign:', err);
      setError(err.message || 'Errore nella richiesta di riassegnazione.');
    }
  };

  // ============================
  // DATI PER LA TABELLA + FILTRI
  // ============================
  const tableItems = useMemo(() => {
  const text = filterText.trim().toLowerCase();

  const getPrevPriority = (s) => {
    if (s === 'Preventivo da fare') return 4;
    if (s === 'A consuntivo') return 3;
    if (s === 'Contratto') return 2;
    if (s === 'Preventivo fatto') return 1;
    return 0;
  };

  const getOrdPriority = (s) => {
    if (s === 'Ordine non ricevuto') return 2;
    if (s === 'Ordine ricevuto') return 1;
    return 0;
  };

  return (tableSource || [])
    .filter((item) => {
      const order = item.workOrder || {};
      const summary = item.summary || {};

      const baseCode =
        order.internalCode ||
        summary.codiceInterno ||
        order.codiceInterno ||
        '-';

      const iter =
        typeof order.iteration === 'number' ? order.iteration : 0;
      const displayCode =
        iter > 0 ? `${baseCode}-${iter}` : baseCode;

      const target = [
        displayCode,
        order.externalCode,
        order.companyName,
        summary.cliente,
        order.place,
        summary.luogo,
        order.equipmentModel,
        order.equipmentSerial,
        order.equipmentInventory,
        order.equipmentPresidio,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (text && !target.includes(text)) return false;

      if (
        filterPreventivo !== 'tutti' &&
        (order.preventivoStatus || '') !== filterPreventivo
      ) {
        return false;
      }

      if (
        filterOrdine !== 'tutti' &&
        (order.orderStatus || '') !== filterOrdine
      ) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      const oa = a.workOrder || {};
      const ob = b.workOrder || {};

      const pA = getPrevPriority(oa.preventivoStatus);
      const pB = getPrevPriority(ob.preventivoStatus);
      if (pA !== pB) return pB - pA; // prima i più urgenti

      const oA = getOrdPriority(oa.orderStatus);
      const oB = getOrdPriority(ob.orderStatus);
      if (oA !== oB) return oB - oA; // poi per ordine

      const dA = new Date(oa.receivedAt || 0).getTime();
      const dB = new Date(ob.receivedAt || 0).getTime();
      return dB - dA; // infine i più recenti
    });
}, [tableSource, filterText, filterPreventivo, filterOrdine]);


  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* Header */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Lavorazioni in corso
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Lavorazioni per cui è stato compilato un report e che sono ancora
              aperte o sospese. A parità di numero interno viene mostrata solo
              l&apos;ultima versione.
            </p>
          </div>
          <button
            onClick={() => navigate('/lavorazioni')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </Card>

        {/* CARDS */}
        {loading ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Caricamento lavorazioni in corso...
          </Card>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-6 text-sm text-red-600">
            {error}
          </div>
        ) : items.length === 0 ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Al momento non ci sono lavorazioni in corso.
          </Card>
        ) : (
          <>
            <div className="max-h-[520px] overflow-y-auto pr-1">
              <div className="grid gap-4 md:grid-cols-2">
              {items.map((item) => {
                const order = item.workOrder || {};
                const summary = item.summary || {};
                const isManutenzione = order.type === 'manutenzione';

                const baseCode =
                  order.internalCode ||
                  summary.codiceInterno ||
                  order.codiceInterno ||
                  '-';
                const iter =
                  typeof order.iteration === 'number' ? order.iteration : 0;
                const displayCode =
                  iter > 0 ? `${baseCode}-${iter}` : baseCode;

                return (
                  <Card
                    key={item._id}
                    className="shadow-md p-4 flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {isManutenzione ? 'Manutenzione' : 'Intervento tecnico'}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                          {displayCode}
                        </span>
                        {order.status === 'sospesa' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-semibold uppercase tracking-wide">
                            In attesa di accettazione
                          </span>
                        )}
                      </div>
                    </div>

                    <h2 className="text-sm font-semibold text-gray-800 mb-1">
                      {order.companyName ||
                        summary.cliente ||
                        'Cliente non specificato'}
                    </h2>

                    <p className="text-xs text-gray-500 mb-1">
                      {order.place || summary.luogo || 'Luogo non specificato'}
                    </p>

                    <div className="text-[11px] text-gray-500 mb-2 space-y-0.5">
                      <p>
                        <span className="font-semibold">Data intervento:</span>{' '}
                        {formatDate(summary.data || item.generatedAt)}
                      </p>
                      <p>
                        <span className="font-semibold">
                          Impianto / Presidio:
                        </span>{' '}
                        {order.equipmentPresidio || summary.impianto || '-'}
                      </p>
                      <p>
                        <span className="font-semibold">Matricola:</span>{' '}
                        {summary.matricolaImpianto ||
                          summary.matricola ||
                          order.equipmentSerial ||
                          '-'}
                      </p>
                      <p>
                        <span className="font-semibold">Inventario:</span>{' '}
                        {summary.inventario || order.equipmentInventory || '-'}
                      </p>
                    </div>

                    {summary.numeroIntervento && (
                      <p className="text-[11px] text-gray-500 mb-2">
                        <span className="font-semibold">Rif. chiamata:</span>{' '}
                        {summary.numeroIntervento}
                      </p>
                    )}

                    <p className="text-[11px] text-gray-400">
                      Report generato il {formatDate(item.generatedAt)}
                    </p>

                    <button
                      type="button"
                      onClick={() => openReport(item)}
                      className="mt-3 inline-flex items-center justify-center px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                    >
                      {isManutenzione ? 'Apri manutenzione' : 'Apri VIT'}
                    </button>

                    {order.status === 'sospesa' && !isManutenzione && (
                      <button
                        type="button"
                        onClick={() => openReassignModal(item)}
                        className="mt-2 inline-flex items-center justify-center px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600"
                      >
                        Riassegna VIT
                      </button>
                    )}
                  </Card>
                );
              })}
             </div> 
            </div>

            {/* TABELLA RIEPILOGO SOTTO LE BOX */}
            <Card className="mt-8 shadow-sm p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    Riepilogo lavorazioni attive
                  </h2>
                  <p className="text-xs text-gray-500">
                    Filtra per codice, cliente, luogo, modello, seriale. Le
                    informazioni chiave su{' '}
                    <span className="font-semibold">stato preventivo</span> e{' '}
                    <span className="font-semibold">stato ordine</span> sono
                    evidenziate.
                  </p>
                </div>

                <div className="flex flex-col md:flex-row gap-2 md:items-center">
                  <input
                    type="text"
                    placeholder="Cerca per codice, cliente, luogo, modello..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="w-full md:w-64 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2">
                    <select
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      value={filterPreventivo}
                      onChange={(e) => setFilterPreventivo(e.target.value)}
                    >
                      <option value="tutti">Tutti i preventivi</option>
                      <option value="Preventivo da fare">
                        Preventivo da fare
                      </option>
                      <option value="Preventivo fatto">Preventivo fatto</option>
                      <option value="A consuntivo">A consuntivo</option>
                      <option value="Contratto">Contratto</option>
                    </select>
                    <select
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      value={filterOrdine}
                      onChange={(e) => setFilterOrdine(e.target.value)}
                    >
                      <option value="tutti">Tutti gli ordini</option>
                      <option value="Ordine ricevuto">Ordine ricevuto</option>
                      <option value="Ordine non ricevuto">
                        Ordine non ricevuto
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              {tableItems.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Nessuna lavorazione corrisponde ai filtri impostati.
                </p>
              ) : (
               <div className="max-h-[520px] overflow-auto border border-gray-200 rounded-lg -mx-4 md:mx-0">
                <table className="min-w-max w-full border-separate border-spacing-0 text-[11px] md:text-xs">
                  <colgroup>
                    <col style={{ width: 140 }} /> {/* Cod. interno */}
                    <col style={{ width: 170 }} /> {/* Cod. esterno */}
                    <col style={{ width: 160 }} /> {/* Tipo */}
                  </colgroup>                                
                    <thead className="sticky top-0 z-40">
                      <tr className="bg-gray-50 text-gray-700">
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-left bg-gray-50 md:sticky md:top-0 md:left-0 md:z-50">
                          Cod. interno
                        </th>
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-left bg-gray-50 sticky md:top-0 md:left-[140px] md:z-50">
                          Cod. esterno
                        </th>
                        <th className="border-b border-r border-gray-200 px-2 py-1 text-left bg-gray-50 sticky md:top-0 md:left-[310px] md:z-50 shadow-[4px_0_0_0_rgba(0,0,0,0.04)]">
                          Tipo
                        </th>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Cliente
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Luogo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Modello
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Seriale
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Inventario
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Impianto / Presidio
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Stato preventivo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Consuntivo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Stato ordine
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Materiale arrivato
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Stato lavorazione
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Data ricezione
                        </TableHeadCell>
                      </tr>
                    </thead>
                    <tbody>
                      {tableItems.map((item) => {
                        const order = item.workOrder || {};
                        const summary = item.summary || {};

                        const baseCode =
                          order.internalCode ||
                          summary.codiceInterno ||
                          order.codiceInterno ||
                          '-';
                        const iter =
                          typeof order.iteration === 'number'
                            ? order.iteration
                            : 0;
                        const displayCode =
                          iter > 0 ? `${baseCode}-${iter}` : baseCode;

                        return (
                          <tr
                            key={item._id}
                            className="border-b last:border-b-0 hover:bg-gray-50"
                          >
                            <td className="border-b border-r border-gray-200 px-2 py-1 bg-white md:sticky md:left-0 md:z-30 whitespace-nowrap">
                              {displayCode}
                            </td>
                            <td className="border-b border-r border-gray-200 px-2 py-1 bg-white md:sticky md:left-[140px] md:z-30 whitespace-nowrap">
                              {order.externalCode || '-'}
                            </td>
                            <td className="border-b border-r border-gray-200 px-2 py-1 bg-white md:sticky md:left-[310px] md:z-30 whitespace-nowrap shadow-[4px_0_0_0_rgba(0,0,0,0.04)]">
                              {order.type === 'manutenzione' ? 'Manutenzione' : 'Intervento tecnico'}
                            </td>
                            <TableCell className="px-2 py-1">
                              {order.companyName ||
                                summary.cliente ||
                                '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.place || summary.luogo || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.equipmentModel || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.equipmentSerial || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.equipmentInventory || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.equipmentPresidio || '-'}
                            </TableCell>
                            {/* PRIORITÀ VISIVA SU STATO PREVENTIVO / STATO ORDINE */}
                            <TableCell className="px-2 py-1">
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                                {order.preventivoStatus || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {order.preventivoStatus === 'A consuntivo'
                                ? (order.consuntivoStatus || '—')
                                : '—'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                {order.orderStatus || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  order.materialeArrivato === 'Si'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : order.materialeArrivato === 'No'
                                    ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                                }`}
                              >
                                {order.materialeArrivato || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {formatWorkStatus(order.status)}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {formatDate(order.receivedAt)}
                            </TableCell>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}

        <div className="md:hidden flex justify-center">
          <button
            onClick={() => navigate('/lavorazioni')}
            className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </div>
      </div>

      {/* MODALE RIASSEGNAZIONE VIT */}
      {reassignModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Riassegna VIT
              </h2>
              <button
                type="button"
                onClick={() => setReassignModal({ open: false, item: null })}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <p className="text-[11px] text-gray-500 mb-3">
              Imposta una nuova data di ricezione e rimanda la lavorazione in{' '}
              <span className="font-semibold">Lavorazioni aperte</span>, con
              utente <span className="font-semibold">Da Assegnare</span> e
              stato del preventivo / ordine aggiornati.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Nuova data ricezione
                </label>
                <input
                  type="date"
                  required
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={reassignForm.dateReceived}
                  onChange={(e) =>
                    setReassignForm((prev) => ({
                      ...prev,
                      dateReceived: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700">
                  Utente a cui assegnare
                </label>
                <select
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                  value={reassignForm.assignedTo}
                  onChange={(e) =>
                    setReassignForm((prev) => ({
                      ...prev,
                      assignedTo: e.target.value,
                    }))
                  }
                >
                  <option value="">Seleziona utente...</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.nome} {u.cognome} ({u.role})
                    </option>
                  ))}
                </select>
              </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Stato preventivo
                  </label>
                  <select
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                    value={reassignForm.preventivoStatus}
                    onChange={(e) =>
                      setReassignForm((prev) => ({
                        ...prev,
                        preventivoStatus: e.target.value,
                        consuntivoStatus:
                          e.target.value === 'A consuntivo' ? prev.consuntivoStatus : '',
                      }))
                    }
                  >
                    <option value="">Seleziona stato...</option>
                    <option value="Preventivo da fare">Preventivo da fare</option>
                    <option value="Preventivo fatto">Preventivo fatto</option>
                    <option value="A consuntivo">A consuntivo</option>
                    <option value="Contratto">Contratto</option>
                  </select>
                </div>

                {reassignForm.preventivoStatus === 'A consuntivo' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      A consuntivo
                    </label>
                    <select
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                      value={reassignForm.consuntivoStatus}
                      onChange={(e) =>
                        setReassignForm((prev) => ({
                          ...prev,
                          consuntivoStatus: e.target.value,
                        }))
                      }
                    >
                      <option value="">Seleziona</option>
                      <option value="Si">Sì</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Stato ordine
                  </label>
                  <select
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                    value={reassignForm.orderStatus}
                    onChange={(e) =>
                      setReassignForm((prev) => ({
                        ...prev,
                        orderStatus: e.target.value,
                      }))
                    }
                  >
                    <option value="">Seleziona stato ordine...</option>
                    <option value="Ordine ricevuto">Ordine ricevuto</option>
                    <option value="Ordine non ricevuto">
                      Ordine non ricevuto
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Materiale arrivato
                  </label>
                  <select
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                    value={reassignForm.materialeArrivato}
                    onChange={(e) =>
                      setReassignForm((prev) => ({
                        ...prev,
                        materialeArrivato: e.target.value,
                      }))
                    }
                  >
                    <option value="">Seleziona...</option>
                    <option value="Si">Sì</option>
                    <option value="No">No</option>
                  </select>
                </div>
                
            </div>

            {error && (
              <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReassignModal({ open: false, item: null })}
                className="px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleReassign}
                className="px-3 py-1.5 rounded-lg text-xs bg-blue-600 text-white hover:bg-blue-700 font-semibold"
              >
                Conferma riassegnazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LavorazioniInCorso;
